// iter_ONLINE_CUSTOMER_BOOKINGS (2026-02-28) — Public booking page.
//
// Mounted at /book/:venueId (also /book?venue=<id> for legacy URLs).
// Unauthenticated. Mobile-first. Reuses the shared schedule helpers
// so venue-local <input type="date"> and slot HH:MM values round-trip
// without timezone drift.
//
// data-testids: pb-page, pb-name, pb-email, pb-phone, pb-party, pb-date,
// pb-time-{HH:MM}, pb-notes, pb-byo-yes, pb-byo-no, pb-byo-ack,
// pb-submit, pb-confirmation, pb-disabled, pb-error-{code}.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayLocal = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function fmtTime12(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  const ampm = h < 12 ? "am" : "pm";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")}${ampm}`;
}

function readClientToken(venueId) {
  const k = `mm_public_booking_ct:${venueId}`;
  try {
    let v = sessionStorage.getItem(k);
    if (!v) {
      v = crypto?.randomUUID?.() || `ct-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(k, v);
    }
    return v;
  } catch { return `ct-${Date.now()}`; }
}

export default function PublicBooking() {
  const { venueId: paramVenueId } = useParams();
  const [sp] = useSearchParams();
  const venueId = paramVenueId || sp.get("venue") || "";

  const [venue, setVenue] = useState(null);
  const [pageState, setPageState] = useState("loading"); // loading|ready|disabled|error
  const [errorMsg, setErrorMsg] = useState("");
  const [slotsResp, setSlotsResp] = useState({ slots: [], reason: null });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [party, setParty] = useState(2);
  const [date, setDate] = useState(todayLocal());
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [byo, setByo] = useState(false);
  const [byoAck, setByoAck] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // ── Load venue + settings ────────────────────────────────────────
  useEffect(() => {
    if (!venueId) { setPageState("error"); setErrorMsg("Missing venue"); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/public/bookings/venue/${venueId}`);
        setVenue(r.data);
        setPageState(r.data.online_bookings_enabled ? "ready" : "disabled");
      } catch (e) {
        setPageState("error");
        setErrorMsg(e?.response?.data?.detail || "Unable to load venue.");
      }
    })();
  }, [venueId]);

  // ── Load slots for the selected date ─────────────────────────────
  const loadSlots = useCallback(async () => {
    if (!venueId || !date) return;
    try {
      const r = await axios.get(`${API}/public/bookings/${venueId}/slots`,
                                 { params: { date } });
      setSlotsResp(r.data);
      if (time && !(r.data.slots || []).some((s) => s.time === time && s.available)) {
        setTime("");
      }
    } catch (e) {
      setSlotsResp({ slots: [], reason: "load_error" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, date]);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  const byoAvailableAtSlot = useMemo(() => {
    if (!venue?.byo?.enabled || !time) return false;
    const s = (slotsResp.slots || []).find((s) => s.time === time);
    return !!(s && s.byo_available);
  }, [venue, time, slotsResp]);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!name.trim() || !email.trim() || !phone.trim()) return false;
    if (!date || !time) return false;
    if (party < 1) return false;
    if (byo) {
      if (venue?.byo?.require_acknowledgement && !byoAck) return false;
      if (!byoAvailableAtSlot) return false;
    }
    return true;
  }, [submitting, name, email, phone, date, time, party, byo, byoAck,
      venue, byoAvailableAtSlot]);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        customer_name: name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim(),
        party_size: Number(party),
        booking_date: date,
        booking_time: time,
        notes: notes.trim(),
        client_token: readClientToken(venueId),
        byo_requested: byo,
        byo_acknowledged: byo && byoAck,
        website,
      };
      const r = await axios.post(`${API}/public/bookings/${venueId}`, body);
      // iter_ONLINE_BOOKINGS_ATOMIC_TRANSITIONS_AND_RESPONSE_FIX
      // (2026-02-28) — server now returns a stable envelope:
      //   { ok, booking:{...}, confirmation_email:{status} }
      // The confirmation screen consumes {booking, email_status}.
      setConfirmation({
        ...(r.data.booking || {}),
        email_status: r.data.confirmation_email?.status || "not_configured",
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : (detail?.message || "Sorry — we couldn't complete your booking.");
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  // ── RENDER ───────────────────────────────────────────────────────
  if (pageState === "loading") {
    return <div className="p-6 text-center text-stone-500" data-testid="pb-loading">Loading…</div>;
  }
  if (pageState === "error") {
    return <div className="p-6 text-center text-red-600" data-testid="pb-error-load">{errorMsg}</div>;
  }
  if (pageState === "disabled") {
    return (
      <div className="min-h-screen bg-stone-50 p-6 sm:p-10" data-testid="pb-disabled">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-6 text-center">
          <h1 className="text-xl font-semibold">{venue?.venue?.name}</h1>
          <p className="mt-3 text-stone-600">Online bookings are not available right now.</p>
          <p className="mt-2 text-sm text-stone-500">Please contact the venue directly to make a booking.</p>
          {venue?.venue?.phone && (
            <a href={`tel:${venue.venue.phone}`} className="mt-3 inline-block text-terracotta-700 underline">
              {venue.venue.phone}
            </a>
          )}
        </div>
      </div>
    );
  }

  if (confirmation) {
    return <ConfirmationScreen conf={confirmation} venue={venue?.venue} />;
  }

  const v = venue?.venue || {};
  const settings = venue?.settings || {};
  const byoCfg = venue?.byo || {};
  const maxPartyServer = settings.max_online_party_size || 12;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="pb-page">
      <div className="max-w-md mx-auto p-4 sm:p-6">
        <div className="bg-white rounded-lg shadow-sm p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-2">
            {v.logo_url && <img src={v.logo_url} alt="" className="h-10 w-10 rounded object-cover" />}
            <div>
              <h1 className="text-lg font-semibold" data-testid="pb-venue-name">{v.name || "Book a table"}</h1>
              {v.address && <p className="text-xs text-stone-500">{v.address}</p>}
            </div>
          </div>
          <p className="text-xs text-stone-500 mb-4">Please provide your details to book a table.</p>

          {/* honeypot */}
          <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)}
                 tabIndex={-1} autoComplete="off"
                 className="hidden" data-testid="pb-honeypot" />

          <Field label="Your name">
            <input data-testid="pb-name" value={name} onChange={(e) => setName(e.target.value)}
                   className="input w-full h-11 px-3 border rounded-md" autoComplete="name" />
          </Field>
          <Field label="Email">
            <input data-testid="pb-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   className="input w-full h-11 px-3 border rounded-md" autoComplete="email" />
          </Field>
          <Field label="Phone">
            <input data-testid="pb-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                   className="input w-full h-11 px-3 border rounded-md" autoComplete="tel" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Diners">
              <input data-testid="pb-party" type="number" min={1} max={maxPartyServer}
                     value={party} onChange={(e) => setParty(Math.max(1, Number(e.target.value) || 1))}
                     className="input w-full h-11 px-3 border rounded-md" />
            </Field>
            <Field label="Date">
              <input data-testid="pb-date" type="date" value={date}
                     min={todayLocal()}
                     onChange={(e) => setDate(e.target.value)}
                     className="input w-full h-11 px-3 border rounded-md" />
            </Field>
          </div>

          <Field label="Time">
            <div className="flex flex-wrap gap-2" data-testid="pb-time-list">
              {(slotsResp.slots || []).length === 0 && (
                <p className="text-sm text-stone-500" data-testid="pb-no-slots">
                  {slotsResp.reason === "day_closed" ? "This day is not open for bookings."
                   : slotsResp.reason === "date_in_past" ? "Please choose a future date."
                   : slotsResp.reason === "beyond_max_advance" ? "This date is too far in the future."
                   : "No available times."}
                </p>
              )}
              {(slotsResp.slots || []).map((s) => (
                <button key={s.time} type="button"
                        data-testid={`pb-time-${s.time}`}
                        disabled={!s.available}
                        onClick={() => setTime(s.time)}
                        className={`h-9 px-3 rounded-md text-sm border ${
                          time === s.time ? "bg-stone-900 text-white border-stone-900" :
                          s.available ? "bg-white hover:bg-stone-100 border-stone-300" :
                          "bg-stone-100 text-stone-400 border-stone-200 cursor-not-allowed"}`}>
                  {fmtTime12(s.time)}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Notes (optional)">
            <textarea data-testid="pb-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2} maxLength={500}
                      className="input w-full px-3 py-2 border rounded-md" />
          </Field>

          {/* BYO section */}
          {byoCfg.enabled && (
            <div className="mt-4 rounded-lg border border-stone-200 p-3" data-testid="pb-byo-section">
              <p className="text-sm font-medium">Bringing your own drinks?</p>
              <div className="flex gap-4 mt-2">
                <label className="text-sm">
                  <input type="radio" name="byo" checked={!byo}
                         data-testid="pb-byo-no" onChange={() => { setByo(false); setByoAck(false); }} /> No
                </label>
                <label className="text-sm">
                  <input type="radio" name="byo" checked={byo}
                         data-testid="pb-byo-yes" onChange={() => setByo(true)}
                         disabled={!byoAvailableAtSlot && !!time} /> Yes
                </label>
              </div>
              {byo && !byoAvailableAtSlot && time && (
                <p className="text-xs text-red-600 mt-2" data-testid="pb-byo-unavailable">
                  BYO is not available at this time.
                </p>
              )}
              {byo && (
                <>
                  <p className="text-sm mt-3">
                    <strong>Corkage fee:</strong> ${Number(byoCfg.corkage_fee || 0).toFixed(2)}
                  </p>
                  <pre className="text-xs whitespace-pre-wrap mt-2 bg-stone-50 p-2 rounded border border-stone-200"
                       data-testid="pb-byo-conditions">{byoCfg.conditions_text}</pre>
                  {byoCfg.require_acknowledgement && (
                    <label className="flex gap-2 mt-3 text-sm">
                      <input type="checkbox" checked={byoAck}
                             onChange={(e) => setByoAck(e.target.checked)}
                             data-testid="pb-byo-ack" />
                      <span>I have read and agree to the venue's BYO conditions, including the corkage fee, seated-patrons-only requirement, identification requirements, and responsible service conditions.</span>
                    </label>
                  )}
                </>
              )}
            </div>
          )}

          <button data-testid="pb-submit"
                  disabled={!canSubmit}
                  onClick={submit}
                  className={`w-full mt-5 h-11 rounded-md font-semibold text-white ${
                    canSubmit ? "bg-stone-900 hover:bg-stone-800" : "bg-stone-300 cursor-not-allowed"}`}>
            {submitting ? "Booking…" : "Book Now"}
          </button>

          <p className="text-[11px] text-stone-500 mt-3">
            By booking you agree to receive booking-related emails from {v.name}.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-3">
      <span className="block text-xs uppercase tracking-wider text-stone-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function ConfirmationScreen({ conf, venue }) {
  const isPending = conf.status === "pending";
  const byo = conf.byo_requested;
  const byoStatus = conf.byo_status;
  // iter_ONLINE_BOOKINGS_ATOMIC_TRANSITIONS_AND_RESPONSE_FIX
  // (2026-02-28) — email message wording follows the operator-
  // approved contract exactly. Never claim an email was sent when
  // it wasn't. Never leak provider errors to the customer.
  const emailStatus = conf.email_status || "not_configured";
  const emailMsg = (
    emailStatus === "sent"
      ? `A confirmation has been emailed to ${conf.customer_email}.`
      : emailStatus === "not_configured"
        ? "Your booking has been saved, but email confirmations are not currently available. Please keep your booking reference or contact the venue."
        : "Your booking has been saved, but we could not send the confirmation email. Please keep your booking reference or contact the venue."
  );
  return (
    <div className="min-h-screen bg-stone-50 p-4 sm:p-8" data-testid="pb-confirmation">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-lg font-semibold">
          {isPending ? "Booking request received" : "Booking confirmed"}
        </h1>
        {isPending && (
          <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            Your booking request is awaiting confirmation from the venue.
          </p>
        )}
        <dl className="mt-4 text-sm space-y-1">
          <Row k="Reference" v={conf.booking_reference} testid="pb-conf-ref" />
          <Row k="Name" v={conf.customer_name} />
          <Row k="Date" v={conf.booking_date} />
          <Row k="Time" v={fmtTime12(conf.booking_time)} testid="pb-conf-time" />
          <Row k="Diners" v={conf.party_size} />
          <Row k="Venue" v={venue?.name} />
          {venue?.address && <Row k="Address" v={venue.address} />}
          {venue?.phone && <Row k="Phone" v={venue.phone} />}
        </dl>
        {byo && (
          <div className="mt-4 rounded border border-stone-200 p-3 text-sm" data-testid="pb-conf-byo">
            <p className="font-medium">BYO requested: Yes</p>
            <p>Status: {byoStatus}</p>
            {conf.byo_corkage_fee != null && (
              <p>Corkage fee: ${Number(conf.byo_corkage_fee).toFixed(2)}</p>
            )}
            <p className="text-xs text-stone-600 mt-2">
              BYO is available to seated patrons only and remains subject to responsible service requirements and staff discretion.
              {byoStatus === "requested" && " Your BYO request is awaiting venue approval."}
            </p>
          </div>
        )}
        <p className="mt-4 text-sm text-stone-700" data-testid={`pb-email-status-${emailStatus}`}>
          {emailMsg}
        </p>
      </div>
    </div>
  );
}

function Row({ k, v, testid }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-stone-500">{k}</dt>
      <dd className="font-medium text-right" data-testid={testid}>{v}</dd>
    </div>
  );
}
